/**
 * Week-by-week planned-vs-actual grouped bar chart.
 *
 * NOTE FOR ORCHESTRATOR: the Klaret spec calls for ECharts (series-1 claret).
 * `echarts` is a dependency of `@teivor/shared-ui` only and is NOT resolvable
 * from `client-portal` (pnpm-isolated), and shared-ui exposes no chart wrapper.
 * Adding the dep / a shared <EChart> would require editing package.json or
 * shared-ui — both outside this jail. This is a dependency-free, token-only SVG
 * fallback that honours the Klaret palette (series-1 = claret = "actual",
 * planned = sunken/hairline), tabular numerals, and reduced-motion (no animation).
 * Swap for the ECharts component once it exists.
 */
import * as React from 'react';

import type { WeekStatusFigure } from '../types';

export interface WeekByWeekChartProps {
  weeks: WeekStatusFigure[];
  /** Short axis label for each week (already localized). */
  labelFor: (weekStart: string, index: number) => string;
  plannedLabel: string;
  actualLabel: string;
}

const CHART_H = 220;
const PAD_TOP = 12;
const PAD_BOTTOM = 28;
const PAD_LEFT = 36;
const GROUP_GAP = 16;

export function WeekByWeekChart({ weeks, labelFor, plannedLabel, actualLabel }: WeekByWeekChartProps) {
  const max = React.useMemo(
    () => Math.max(1, ...weeks.flatMap((w) => [w.planned, w.actual])),
    [weeks],
  );

  // Nice round upper bound for the axis (so the top gridline reads cleanly).
  const axisMax = React.useMemo(() => niceCeil(max), [max]);

  const groupW = 56;
  const barW = 18;
  const innerGap = 6;
  const plotH = CHART_H - PAD_TOP - PAD_BOTTOM;
  const width = PAD_LEFT + weeks.length * groupW + (weeks.length + 1) * GROUP_GAP;

  const y = (v: number) => PAD_TOP + plotH - (v / axisMax) * plotH;

  // 3 horizontal gridlines + baseline.
  const gridValues = [axisMax, axisMax / 2, 0];

  return (
    <figure className="m-0 w-full" aria-label={`${plannedLabel} / ${actualLabel}`}>
      {/* Legend */}
      <figcaption className="mb-3 flex items-center gap-4 text-[12px] leading-[16px] text-[var(--tvl-ink-2)]">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-[2px] border border-[var(--tvl-hairline)] bg-[var(--tvl-sunken)]"
            aria-hidden
          />
          {plannedLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-[2px] bg-[var(--tvl-accent)]"
            aria-hidden
          />
          {actualLabel}
        </span>
      </figcaption>

      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${CHART_H}`}
          width="100%"
          height={CHART_H}
          role="img"
          preserveAspectRatio="xMinYMid meet"
          style={{ minWidth: width, fontFamily: 'var(--tvl-font-mono)' }}
        >
          {/* gridlines + y labels */}
          {gridValues.map((gv) => (
            <g key={gv}>
              <line
                x1={PAD_LEFT}
                x2={width}
                y1={y(gv)}
                y2={y(gv)}
                stroke="var(--tvl-hairline)"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 8}
                y={y(gv) + 4}
                textAnchor="end"
                className="tabular-nums"
                fontSize={11}
                fill="var(--tvl-ink-3)"
              >
                {Math.round(gv)}
              </text>
            </g>
          ))}

          {/* groups */}
          {weeks.map((w, i) => {
            const gx = PAD_LEFT + GROUP_GAP + i * (groupW + GROUP_GAP);
            const plannedX = gx + (groupW - (barW * 2 + innerGap)) / 2;
            const actualX = plannedX + barW + innerGap;
            return (
              <g key={w.weekStart}>
                {/* planned */}
                <rect
                  x={plannedX}
                  y={y(w.planned)}
                  width={barW}
                  height={Math.max(0, PAD_TOP + plotH - y(w.planned))}
                  rx={2}
                  fill="var(--tvl-sunken)"
                  stroke="var(--tvl-hairline)"
                  strokeWidth={1}
                >
                  <title>{`${plannedLabel}: ${w.planned}`}</title>
                </rect>
                {/* actual — series-1 claret */}
                <rect
                  x={actualX}
                  y={y(w.actual)}
                  width={barW}
                  height={Math.max(0, PAD_TOP + plotH - y(w.actual))}
                  rx={2}
                  fill="var(--tvl-accent)"
                >
                  <title>{`${actualLabel}: ${w.actual}`}</title>
                </rect>
                {/* x label */}
                <text
                  x={gx + groupW / 2}
                  y={CHART_H - 10}
                  textAnchor="middle"
                  fontSize={11}
                  fill="var(--tvl-ink-3)"
                >
                  {labelFor(w.weekStart, i)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

function niceCeil(n: number): number {
  if (n <= 5) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const f = n / pow;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * pow;
}
